import './Partner.css';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  requestOTP,
  verifyOTP,
  fetchUser,
  fetchRefData,
  setToken as setCoreToken,
} from '@genfun/core/api/auth';
import { RedButton, RedInput } from '../components/common';
import { logoText } from '../data/navigation';

const COMMISSION_RATE = 30;

/* ------------------------------------------------------------------ */
/* Reporting periods                                                   */
/* The keys are the ones /get_ref_data buckets its data under. The     */
/* backend picks the bucket size per range (hourly for a day, daily up */
/* to a month, monthly beyond), so `granularity` mirrors that choice   */
/* purely to label the axis.                                           */
/* ------------------------------------------------------------------ */

const PERIODS = [
  { key: 'today', label: 'Today', chartTitle: 'Today', granularity: 'hour' },
  { key: 'week', label: 'Week', chartTitle: 'Last 7 days', granularity: 'day' },
  { key: 'month', label: 'Month', chartTitle: 'Last 30 days', granularity: 'day' },
  { key: 'all', label: 'All time', chartTitle: 'Last 12 months', granularity: 'month' },
];

function fmtMoney(n) {
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtInt(n) {
  return n.toLocaleString('en-US');
}

// Bucket names arrive as "%H:%M", "%Y-%m-%d" or "%Y-%m-01" depending on the range,
// so the period tells us which of the last two we're looking at.
function bucketLabel(name, granularity) {
  if (granularity === 'hour') return name;
  const parts = String(name).split('-');
  if (parts.length !== 3) return name;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  if (Number.isNaN(d.getTime())) return name;
  return granularity === 'month'
    ? d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// funnel_data / sales_plots_data for one period -> everything the cards and the
// chart render. Commission is ours, the backend only reports gross sales.
function buildStats(data, period) {
  const funnel = data?.funnel_data?.[period.key] || {};
  const sales = data?.sales_plots_data?.[period.key] || [];

  const series = sales.map((point) => ({
    label: bucketLabel(point.name, period.granularity),
    value: (point.value || 0) * (COMMISSION_RATE / 100),
  }));
  const revenue = sales.reduce((sum, point) => sum + (point.value || 0), 0);

  return {
    visits: funnel.visits || 0,
    signups: funnel.registrations || 0,
    paying: funnel.sales || 0,
    revenue,
    earnings: revenue * (COMMISSION_RATE / 100),
    series,
  };
}

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

function LockIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="6" y1="20" x2="6" y2="12" />
      <line x1="12" y1="20" x2="12" y2="6" />
      <line x1="18" y1="20" x2="18" y2="14" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Sign in (OTP only)                                                  */
/* ------------------------------------------------------------------ */

function PartnerLogin({ onToken }) {
  const emailRef = useRef(null);
  const otpRef = useRef(null);
  const [awaitingCode, setAwaitingCode] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const sendCode = async (isResend = false) => {
    const value = (isResend ? email : emailRef.current?.value)?.trim();
    if (!value) { setError('Please enter your email'); return; }
    if (isResend) setResending(true); else setLoading(true);
    try {
      const ref = localStorage.getItem('referral_code');
      const [hasError, status] = await requestOTP(value, ref);
      if (hasError) {
        if (status === 404) setError('User not found');
        else if (status === 400) setError('This account is registered through Google');
        else setError('Failed to send code. Please try again.');
        return;
      }
      setEmail(value);
      setAwaitingCode(true);
      setError(null);
    } catch (e) {
      setError('An error occurred. Please try again.');
    } finally {
      if (isResend) setResending(false); else setLoading(false);
    }
  };

  const verify = async () => {
    const code = otpRef.current?.value?.trim();
    if (!code) { setError('Please enter the code'); return; }
    setLoading(true);
    try {
      const [response, status] = await verifyOTP(email, code);
      if (status === 200 && response?.access_token) {
        setCoreToken(response.access_token);
        onToken(response.access_token);
        return;
      }
      setError(response?.detail || 'Invalid code. Please try again.');
    } catch (e) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="partnerAuth">
      <div className="partnerAuthCard">
        <div className="partnerAuthIcon"><LockIcon /></div>
        <h1 className="partnerAuthTitle">Partner Access</h1>
        <p className="partnerAuthSubtitle">
          {awaitingCode ? 'Enter the code we emailed you' : 'Sign in with your email to continue'}
        </p>

        {!awaitingCode ? (
          <div className="partnerAuthForm">
            <RedInput inputRef={emailRef} label="Email" placeholder="you@example.com" type="email" />
            <RedButton onClick={() => sendCode(false)} disabled={loading}>
              {loading ? 'Sending…' : 'Send code'}
            </RedButton>
          </div>
        ) : (
          <div className="partnerAuthForm">
            <div className="partnerAuthNote">
              We've sent a code to <span className="partnerAuthEmail">{email}</span>. Check your inbox and spam folder.
            </div>
            <RedInput inputRef={otpRef} label="Code from email" placeholder="Enter code" type="text" />
            <span className="partnerAuthResend" onClick={() => !resending && sendCode(true)}>
              {resending ? 'Sending…' : 'Resend code'}
            </span>
            <RedButton onClick={verify} disabled={loading}>
              {loading ? 'Verifying…' : 'Log in'}
            </RedButton>
            <span
              className="partnerAuthBack"
              onClick={() => { setAwaitingCode(false); setError(null); }}
            >
              ← Use a different email
            </span>
          </div>
        )}

        {error && <div className="partnerAuthError">{error}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dashboard pieces                                                    */
/* ------------------------------------------------------------------ */

function StatCard({ label, value, sub, accent = false }) {
  return (
    <div className={`partnerStat ${accent ? 'partnerStatAccent' : ''}`}>
      <div className="partnerStatLabel">{label}</div>
      <div className="partnerStatValue">{value}</div>
      {sub && <div className="partnerStatSub">{sub}</div>}
    </div>
  );
}

function EarningsChart({ series, title, total }) {
  const max = series.reduce((m, p) => Math.max(m, p.value), 0);
  const isEmpty = max <= 0;

  // The backend only emits buckets it has data for, so the axis reads its own ends
  // rather than assuming a fixed window.
  const axis = isEmpty
    ? []
    : [series[0], series[Math.floor((series.length - 1) / 2)], series[series.length - 1]];

  return (
    <div className="partnerChartCard">
      <div className="partnerChartHead">
        <div>
          <div className="partnerChartKicker">Earnings</div>
          <div className="partnerChartTitle">{title}</div>
        </div>
        <div className="partnerChartTotal">
          <span className="partnerChartTotalLabel">Total</span>
          <span className="partnerChartTotalValue">{fmtMoney(total)}</span>
        </div>
      </div>
      {isEmpty ? (
        <div className="partnerChartEmpty">
          <div className="partnerChartBaseline" aria-hidden />
          <span>No earnings in this period — share your link to get started.</span>
        </div>
      ) : (
        <>
          <div className="partnerChartBars" style={{ '--bars': series.length }}>
            {series.map((point, i) => (
              <div
                key={`${point.label}-${i}`}
                className="partnerBar"
                style={{ '--h': `${(point.value / max) * 100}%` }}
                title={`${point.label}: ${fmtMoney(point.value)}`}
              />
            ))}
          </div>
          <div className="partnerChartAxis">
            {axis.map((point, i) => <span key={i}>{point.label}</span>)}
          </div>
        </>
      )}
    </div>
  );
}

function PeriodSelector({ period, setPeriod }) {
  return (
    <div className="partnerPeriods" role="tablist" aria-label="Reporting period">
      {PERIODS.map((p) => (
        <button
          key={p.key}
          role="tab"
          aria-selected={p.key === period.key}
          className={`partnerPeriod ${p.key === period.key ? 'partnerPeriodActive' : ''}`}
          onClick={() => setPeriod(p)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dashboard                                                           */
/* ------------------------------------------------------------------ */

function PartnerDashboard({ email, onLogout, data, loading, error }) {
  const [period, setPeriod] = useState(PERIODS[2]);

  const stats = buildStats(data, period);
  const signupConv = stats.visits ? ((stats.signups / stats.visits) * 100).toFixed(1) : '0.0';
  const paidConv = stats.signups ? ((stats.paying / stats.signups) * 100).toFixed(1) : '0.0';

  return (
    <div className="partnerDash">
      <header className="partnerDashHeader">
        <div className="partnerDashBrand">
          <span className="partnerDashLogo">
            {logoText.primary}
            <span className="partnerDashLogoAccent">{logoText.accent}</span>
          </span>
          <span className="partnerDashSep">/</span>
          <span className="partnerDashSection"><ChartIcon /> Partner analytics</span>
        </div>
        <div className="partnerDashUser">
          <span className="partnerDashEmail">{email}</span>
          <button className="partnerDashLogout" onClick={onLogout} aria-label="Sign out">
            <LogoutIcon />
          </button>
        </div>
      </header>

      <div className="partnerDashBody">
        <section className="partnerGreet">
          <div className="partnerGreetEyebrow">
            <span className="partnerGreetDot" />
            Partner analytics
          </div>
          <h1 className="partnerGreetTitle">
            Welcome, <em>partner.</em>
          </h1>
          <p className="partnerGreetSub">
            Share your link and start earning. Commission rate: <strong>{COMMISSION_RATE}%</strong> of every sale.
          </p>
        </section>

        <PeriodSelector period={period} setPeriod={setPeriod} />

        {error ? (
          <div className="partnerDataError">{error}</div>
        ) : loading && !data ? (
          <div className="partnerLoading">Loading your stats…</div>
        ) : (
          <>
            <section className="partnerStatsGrid">
              <StatCard label="Visits" value={fmtInt(stats.visits)} sub={period.chartTitle.toLowerCase()} />
              <StatCard label="Sign-ups" value={fmtInt(stats.signups)} sub={`${signupConv}% conv. from visits`} />
              <StatCard label="Paying users" value={fmtInt(stats.paying)} sub={`${paidConv}% conv. from sign-ups`} />
              <StatCard label="Sales revenue" value={fmtMoney(stats.revenue)} sub="gross merchandise value" />
              <StatCard label="Your earnings" value={fmtMoney(stats.earnings)} sub={`${COMMISSION_RATE}% commission`} accent />
            </section>

            <section>
              <EarningsChart
                series={stats.series}
                title={period.chartTitle}
                total={stats.earnings}
              />
            </section>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Root                                                                */
/* ------------------------------------------------------------------ */

export default function Partner({ token, setToken }) {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(!!token);
  const [refData, setRefData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [dataError, setDataError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setUser(null); setLoadingUser(false); return; }
    setLoadingUser(true);
    (async () => {
      const [u, status] = await fetchUser(token);
      if (cancelled) return;
      if (status === 401) {
        setUser(null);
        setToken(null);
      } else if (u) {
        setUser(u);
      }
      setLoadingUser(false);
    })();
    return () => { cancelled = true; };
  }, [token, setToken]);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setRefData(null); setDataError(null); return; }
    setLoadingData(true);
    (async () => {
      const [d, status] = await fetchRefData(token);
      if (cancelled) return;
      if (status === 200) {
        setRefData(d);
        setDataError(null);
      } else if (status === 403) {
        // signed in, but the account carries no referral code to report on
        setDataError("This account doesn't have partner reporting attached yet.");
      } else if (status !== 401) {
        // 401 is the token expiring — the /me effect above already signs us out
        setDataError(d?.detail || 'Could not load your stats. Please try again.');
      }
      setLoadingData(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleLogout = useCallback(() => {
    setUser(null);
    setRefData(null);
    setDataError(null);
    setToken(null);
  }, [setToken]);

  let content;
  if (!token) {
    content = <PartnerLogin onToken={setToken} />;
  } else if (loadingUser) {
    content = (
      <div className="partnerAuth">
        <div className="partnerLoading">Loading…</div>
      </div>
    );
  } else if (user) {
    content = (
      <PartnerDashboard
        email={user.email}
        onLogout={handleLogout}
        data={refData}
        loading={loadingData}
        error={dataError}
      />
    );
  } else {
    // token present but /me gave us nothing usable — let them sign in again
    content = <PartnerLogin onToken={setToken} />;
  }

  return <div className="partnerPage">{content}</div>;
}
